import { useContext, useMemo } from 'react'
import CurrencyContext from 'contexts/currencyContext'
import { shallowEqual, useAppSelector } from 'hooks/reduxHooks'
import { arrayToMap } from 'lib/array/arrayUtils'
import {
  getCartPanelExperienceTimesKey,
  resolveExperiencePricing,
  type ExperienceResolvedPricing,
} from '../utils/cartPanelUtils'
import type { CartPanelExperiencePricingInput } from '../utils/tripChatCartPricingInputUtils'

export type ExperiencePricingInput = CartPanelExperiencePricingInput

export function useExperienceCartItemPricing(
  items: Array<ExperiencePricingInput>,
  waitForSelectedTicketTimes = true,
): Map<number, ExperienceResolvedPricing> {
  const currency = useContext(CurrencyContext)
  const { experiences, fetchingExperiences, experienceErrors, experienceTimes } = useAppSelector(state => ({
    experiences: state.experience.experiences,
    fetchingExperiences: state.experience.fetchingExperiences,
    experienceErrors: state.experience.experienceErrors,
    experienceTimes: state.experience.experienceTimes,
  }), shallowEqual)

  return useMemo(() => {
    return arrayToMap(items, item => item.tripItemId, item => {
      const experience = experiences[item.experienceId]
      const key = getCartPanelExperienceTimesKey(item, currency)
      const timeSlotState = experienceTimes[item.experienceId]?.[key]
      const error = !!experienceErrors[item.experienceId] || !!timeSlotState?.error
      const waitsForSelectedTicketTimes = waitForSelectedTicketTimes && item.tickets.length > 0 && !timeSlotState?.slots && !error
      const loading = !!fetchingExperiences[item.experienceId] || !!timeSlotState?.fetching || waitsForSelectedTicketTimes

      return resolveExperiencePricing({
        experience,
        timeSlots: timeSlotState?.slots,
        tickets: item.tickets,
        date: item.date,
        time: item.time,
        isBuyNowBookLater: item.isBuyNowBookLater,
        savedPrice: item.savedPrice,
        savedMemberPrice: item.savedMemberPrice,
        savedValue: item.savedValue,
        cartPrice: item.cartPrice,
        cartMemberPrice: item.cartMemberPrice,
        loading,
        error,
      })
    })
  }, [items, waitForSelectedTicketTimes, experiences, fetchingExperiences, experienceErrors, experienceTimes, currency])
}
